import React, { FocusEvent, ChangeEvent } from 'react';
import { Form } from 'react-bootstrap';
import { InputFieldState } from '../types';

/**
 * IdentificationNumberFormコンポーネント用のPropsです。
 * @interface IdentificationNumberFormProps
 */
interface IdentificationNumberFormProps {
  /**
   * 識別番号
   * @type {InputFieldState}
   * @memberof IdentificationNumberFormProps
   */
  identificationNumber: InputFieldState;

  /**
   * 代理人フラグ
   * @type {boolean}
   * @memberof AddressFormProps
   */
  isAgency: boolean;

  /**
   * 識別番号を設定する関数
   * @memberof IdentificationNumberFormProps
   */
  onChange: (identificationNumber: InputFieldState) => void;

  /**
   * 必須フラグ
   * @type {boolean}
   * @memberof IdentificationNumberFormProps
   */
  isRequired: boolean;

}

/**
 * 識別番号フォームのコンポーネントです。
 * @param {string} props.postalCode - 表示する識別番号
 * @param {function} props.onChange - 識別番号が変更されたときに呼ばれるコールバック関数
 * @returns {JSX.Element} - 識別番号フォームのJSX要素
 */
const IdentificationNumberForm: React.FC<IdentificationNumberFormProps> = ({ identificationNumber, isAgency, onChange, isRequired }) => {

  /**
   * フォームからフォーカスが外れたときのイベントハンドラーです。
   * 入力された識別番号を整形し、onChange関数を呼び出します。
   * @param {object} event - フォーカスアウトのイベント
   * @returns {void}
   */
  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {

    const halfWidthText = event.target.value.replace(/[Ａ-Ｚａ-ｚ０-９]/g, function (s) {
      return String.fromCharCode(s.charCodeAt(0) - 0xfee0);
    });
    const deleteNonNumberInputValue = halfWidthText.replace(/[^0-9]/g, '').substring(0, 9);
    const formatIdentificationNumber: string = deleteNonNumberInputValue ? ('000000000' + deleteNonNumberInputValue).slice(-9) : deleteNonNumberInputValue;

    let errorMessage = '';
    const regexp = /\d{9}/;

    if(formatIdentificationNumber === '') {
      // 識別番号が空で、必須フラグあり（申出人又は代理人）の場合はエラー
      if(isRequired) {
        errorMessage = '識別番号が入力されていません。';
      }
    } else if(!regexp.test(formatIdentificationNumber)) {
      // ９桁の数字ではない場合はエラー
      errorMessage = '入力された識別番号の形式（9桁の数字）が正しくありません。';
    }

    onChange({
      value: formatIdentificationNumber,
      errorMessage: errorMessage
    });

  }

  /**
   * フォームの変更イベントハンドラーです。
   * 入力された識別番号をonChange関数を呼び出します。
   * @param {object} event - フォームの変更イベント
   * @returns {void}
   */
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange({
      value: event.target.value,
      errorMessage: identificationNumber.errorMessage
    });
  }

  return (
    <Form.Group className="mb-2 row">
      <Form.Label className="col-lg-2 col-12 d-flex align-items-center">
        識別番号
        {/* 代理人の場合は「条件必須」、申出人の場合は「必須」、権利者の場合は「任意」 */}
        {isAgency ? <span className="text-small text-primary ms-3">※条件必須</span> : (isRequired ? <span className="text-small text-danger ms-3">※必須</span> : <span className="text-small text-secondary ms-3">※任意</span>)}
      </Form.Label>
      <div className="col-lg-10 col-12">
        <Form.Control
          type="text"
          value={identificationNumber.value}
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder="９桁の識別番号を入力してください。"
          aria-label="識別番号"
          isInvalid={identificationNumber.errorMessage !== ''}
          maxLength={9}
        />
        {identificationNumber.errorMessage !== '' ? <div className="text-end text-danger small">{identificationNumber.errorMessage}</div> : ''}
      </div>
    </Form.Group>
  );
}

export default IdentificationNumberForm;